import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService from '../services/authService';

interface UserInfo {
  id: string;
  email: string;
  name: string;
  preferredName?: string;
  emailVerified: boolean;
  attributes?: Record<string, any>;
}

interface AuthContextType {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (
    email: string,
    password: string,
    name: string,
    phone?: string,
    preferredName?: string,
    stylePreferences?: string
  ) => Promise<{ success: boolean; confirmationRequired?: boolean; error?: string }>;
  confirmRegistration: (email: string, code: string) => Promise<boolean>;
  resendConfirmationCode: (email: string) => Promise<boolean>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<boolean>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<boolean>;
  inviteUser: (email: string, message?: string) => Promise<boolean>;
  deleteAccount: () => Promise<boolean>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      setIsLoading(true);
      try {
        if (authService.isAuthenticated()) {
          // CRITICAL: Load user from localStorage immediately to avoid redirect loop
          const currentUser = authService.getCurrentUser();
          if (currentUser) {
            // Set user immediately from localStorage
            setUser(currentUser);
            setIsLoading(false);

            // Then refresh profile from server in background
            try {
              const profile = await authService.getProfile();
              if (profile) {
                setUser(profile);
              }
            } catch (err) {
              console.error('Profile refresh failed:', err);
              // Keep the localStorage user even if refresh fails
            }
          } else {
            // No cached user, fetch from server
            const profile = await authService.getProfile();
            if (profile) {
              setUser(profile);
            }
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const clearError = () => setError(null);

  const login = async (email: string, password: string): Promise<boolean> => {
    setError(null);
    setIsLoading(true);

    try {
      const result = await authService.login(email, password);

      if (result.success) {
        // CRITICAL: Ensure user is set before returning success
        // First try from login response
        if (result.user) {
          setUser(result.user);
          setIsLoading(false);
          return true;
        }

        // Then try from localStorage (authService saves it there)
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setIsLoading(false);
          return true;
        }

        // Finally fetch from server
        const profile = await authService.getProfile();
        if (profile) {
          setUser(profile);
          setIsLoading(false);
          return true;
        }

        // Success but no user data - shouldn't happen but handle it
        setIsLoading(false);
        return true;
      } else {
        setError(result.error || 'Login failed');
        setIsLoading(false);
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during login');
      setIsLoading(false);
      return false;
    }
  };

  const register = async (
    email: string,
    password: string,
    name: string,
    phone?: string,
    preferredName?: string,
    stylePreferences?: string
  ): Promise<{ success: boolean; confirmationRequired?: boolean; error?: string }> => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await authService.register(
        email,
        password,
        name,
        phone,
        preferredName,
        stylePreferences
      );
      
      if (result.success) {
        return {
          success: true,
          confirmationRequired: true,
        };
      } else {
        const errorMessage = result.error || 'Registration failed';
        setError(errorMessage);
        return {
          success: false,
          error: errorMessage,
        };
      }
    } catch (err: any) {
      const errorMessage = err.message || 'An error occurred during registration';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsLoading(false);
    }
  };

  const confirmRegistration = async (email: string, code: string): Promise<boolean> => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await authService.confirmRegistration(email, code);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || 'Confirmation failed');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during confirmation');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resendConfirmationCode = async (email: string): Promise<boolean> => {
    setError(null);
    
    try {
      const result = await authService.resendConfirmationCode(email);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || 'Failed to resend code');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const forgotPassword = async (email: string): Promise<boolean> => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await authService.forgotPassword(email);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || 'Failed to initiate password reset');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<boolean> => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await authService.resetPassword(email, code, newPassword);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || 'Password reset failed');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const changePassword = async (
    oldPassword: string,
    newPassword: string
  ): Promise<boolean> => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await authService.changePassword(oldPassword, newPassword);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || 'Password change failed');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const inviteUser = async (email: string, message?: string): Promise<boolean> => {
    setError(null);
    
    try {
      const result = await authService.inviteUser(email, message);
      
      if (result.success) {
        return true;
      } else {
        setError(result.error || 'Failed to send invitation');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      return false;
    }
  };

  const deleteAccount = async (): Promise<boolean> => {
    setError(null);
    setIsLoading(true);
    
    try {
      const result = await authService.deleteAccount();
      
      if (result.success) {
        setUser(null);
        return true;
      } else {
        setError(result.error || 'Failed to delete account');
        return false;
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const profile = await authService.getProfile();
      if (profile) {
        setUser(profile);
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: authService.isAuthenticated() && !!user,
    isLoading,
    error,
    login,
    register,
    confirmRegistration,
    resendConfirmationCode,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    inviteUser,
    deleteAccount,
    refreshUser,
    clearError,
  };

  // Debug logging
  React.useEffect(() => {
    console.log('🔐 Auth State:', {
      hasToken: authService.isAuthenticated(),
      hasUser: !!user,
      isAuthenticated: authService.isAuthenticated() && !!user,
      isLoading,
      userEmail: user?.email,
    });
  }, [user, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};